/*
 *  This file contains everything needed to tell our sessionCheck service about a user interacting with the website.
 *  That is, any interaction configured here should restart the idle session checking (no longer idle if the user interrupts)
 */

import { EventEmitter } from '@angular/core';
import { Observable, Subscription, fromEvent, merge } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';

/*
 * A class for expressing arguments to interrupt events.
 */
export class InterruptArgs
{
    constructor(public source: InterruptSource, public innerArgs: any, public force = false) { }
}


const defaultThrottleDelay = 500;

export abstract class InterruptSource
{
    isAttached = false;

    public onInterrupt: EventEmitter<InterruptArgs> = new EventEmitter<InterruptArgs>();

    protected constructor(
        protected attachFn?: (source: InterruptSource) => void,
        protected detachFn?: (source: InterruptSource) => void) { }

    /*
     * Attaches to the specified events on the specified source.
     */
    attach(): void {
        if (!this.isAttached && this.attachFn) {
            this.attachFn(this);
        }
        this.isAttached = true;
    }

    /*
     * Detaches from the specified events on the specified source.
     */
    detach(): void {
        if (this.isAttached && this.detachFn) {
            this.detachFn(this);
        }
        this.isAttached = false;
    }
}

/*
 * A class for managing an interrupt from an interrupt source.
 */
export class Interrupt
{
    private sub: Subscription;

    constructor(public source: InterruptSource) { }

    /*
     * Subscribes to the interrupt using the specified function.
     * @param fn - The subscription function.
     */
    subscribe(fn: (args: InterruptArgs) => void): void
    {
        this.sub = this.source.onInterrupt.subscribe(fn);
    }

    /*
     * Unsubscribes the interrupt.
     */
    unsubscribe(): void
    {
        this.sub.unsubscribe();
        this.sub = null;
    }

    /*
     * Keeps the subscription but resumes interrupt events.
     */
    resume(): void
    {
        this.source.attach();
    }

    /*
     * Keeps the subscription but pauses interrupt events.
     */
    pause(): void
    {
        this.source.detach();
    }
}

/*
 * An interrupt source on an EventTarget object, such as a Window or HTMLElement.
 */
export class EventTargetInterruptSource extends InterruptSource
{
    public eventSrce: Observable<any>;
    public eventSubscript: Subscription = new Subscription();
    protected throttleDelay: number;
    protected passive: boolean;

    constructor(protected target: any, protected events: string, options?: number | EventTargetInterruptOptions)
    {
        super(null, null);

        if (typeof options === 'number')
        {
            options = { throttleDelay: options, passive: false };
        }

        options = options || { throttleDelay: defaultThrottleDelay, passive: false };

        if (options.throttleDelay === undefined || options.throttleDelay === null)
        {
            options.throttleDelay = defaultThrottleDelay;
        }

        this.throttleDelay = options.throttleDelay;
        this.passive = !!options.passive;

        const opts = this.passive ? { passive: true } : null;
        const fromEvents = events.split(' ').map(eventName => fromEvent<any>(target, eventName, opts));
        this.eventSrce = merge(...fromEvents);
        this.eventSrce = this.eventSrce.pipe(filter(innerArgs => !this.filterEvent(innerArgs)));
        if (this.throttleDelay > 0)
        {
            this.eventSrce = this.eventSrce.pipe(throttleTime(this.throttleDelay));
        }

        let handler = (innerArgs: any) => this.onInterrupt.emit(new InterruptArgs(this, innerArgs));

        this.attach = () => this.eventSubscript = this.eventSrce.subscribe(handler);

        this.detach = () => this.eventSubscript.unsubscribe();
    }

    /*
     * Checks to see if the event should be filtered. Always returns false unless overriden.
     * @param event - The original event object.
     * @return True if the event should be filtered (don't cause an interrupt); otherwise, false.
     */
    protected filterEvent(event: any): boolean
    {
        return false;
    }

    /**
     * Returns the current options being used.
     * @return {EventTargetInterruptOptions} The current option values.
     */
    get options(): EventTargetInterruptOptions
    {
        return { throttleDelay: this.throttleDelay, passive: this.passive };
    }
}

/**
 * Options for EventTargetInterruptSource
 */
export interface EventTargetInterruptOptions
{
    /**
     * The number of milliseconds to throttle the events coming from the target.
     */
    throttleDelay?: number;

    /**
     * Whether or not to use passive event listeners.
     * Note: you need to detect if the browser supports passive listeners, and only set this to true if it does.
     */
    passive?: boolean;
}

export class DocumentInterruptSource extends EventTargetInterruptSource
{
    constructor(events: string, options?: number | EventTargetInterruptOptions)
    {
        super(document.documentElement, events, options);
    }

    /*
     * Checks to see if the event should be filtered.
     * @param event - The original event object.
     * @return True if the event should be filtered (don't cause an interrupt); otherwise, false.
     */
    filterEvent(event: any): boolean
    {
        // some browser bad input hacks
        if (event.type === 'mousemove'
            // fix for Chrome destop notifications
            && ((event.originalEvent && event.originalEvent.movementX === 0 &&
                    event.originalEvent.movementY === 0)
                // fix for webkit fake mousemove
                || (event.movementX !== void 0 && !event.movementX || !event.movementY)))
        {
            return true;
        }

        return false;
    }
}

/*
 * An interrupt source on the Window object.
 */
export class WindowInterruptSource extends EventTargetInterruptSource
{
    constructor(events: string, options?: number | EventTargetInterruptOptions)
    {
        super(window, events, options);
    }
}


export class StorageInterruptSource extends WindowInterruptSource
{
    constructor(throttleDelay = 500)
    {
        super('storage', throttleDelay);
    }

    /*
     * Checks to see if the event should be filtered.
     * @param event - The original event object.
     * @return True if the event should be filtered (don't cause an interrupt); otherwise, false.
     */
    filterEvent(event: StorageEvent): boolean
    {
        if (event.key.indexOf('myID.') >= 0 && event.key.indexOf('.Expire') >= 0)
        {
            return false;
        }
        return true;
    }
}

export function createDefaultInterruptSources(options?: EventTargetInterruptOptions)
{
    return [new DocumentInterruptSource(
            'mousemove keydown DOMMouseScroll mousewheel mousedown touchstart touchmove scroll', options),
        new StorageInterruptSource()];
}

export const DEFAULT_INTERRUPTSOURCES: any[] = createDefaultInterruptSources();